import React, { useState, useEffect, useCallback } from "react";
import {
  Search, MapPin, Phone, Mail, Globe, Star, RefreshCw,
  MessageSquare, Send, CheckCircle2, XCircle, Clock,
  Users, TrendingUp, Zap, Filter, ChevronDown, Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, CircleMarker, Circle, Tooltip, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import API from "../../api/axiosConfig";

const STATUS_LABELS = {
  nuevo:       "Nuevo",
  contactado:  "Contactado",
  interesado:  "Interesado",
  registrado:  "Registrado",
  descartado:  "Descartado",
};

const STATUS_COLORS = {
  nuevo:       "bg-gray-100 text-gray-600",
  contactado:  "bg-blue-100 text-blue-700",
  interesado:  "bg-amber-100 text-amber-700",
  registrado:  "bg-green-100 text-green-700",
  descartado:  "bg-red-100 text-red-500",
};

// ── Tab: Descubrir (búsqueda por punto en el mapa) ────────────────────────────
const PROSPECT_CATEGORY_OPTIONS = [
  { key: "restaurante", label: "Restaurantes" },
  { key: "comida_rapida", label: "Comida rápida" },
  { key: "cafeteria", label: "Cafeterías" },
  { key: "bar", label: "Bares" },
  { key: "heladeria", label: "Heladerías" },
  { key: "panaderia", label: "Panaderías" },
  { key: "supermercado", label: "Supermercados" },
  { key: "tienda", label: "Tiendas de barrio" },
  { key: "farmacia", label: "Farmacias" },
  { key: "licoreria", label: "Licorerías" },
  { key: "ropa", label: "Ropa y moda" },
  { key: "floristeria", label: "Floristerías" },
  { key: "mascotas", label: "Mascotas" },
  { key: "ferreteria", label: "Ferreterías" },
];

const DEFAULT_CENTER = { lat: 4.711, lng: -74.0721 }; // Bogotá — punto de partida, se mueve libremente

function MapClickPicker({ onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => { map.setView([center.lat, center.lng], map.getZoom()); }, [center, map]);
  return null;
}

function DiscoverTab() {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [point, setPoint] = useState(null);
  const [radiusKm, setRadiusKm] = useState(1);
  const [country, setCountry] = useState("Colombia");
  const [cats, setCats] = useState([]);
  const [searching, setSearching] = useState(false);
  const [places, setPlaces] = useState(null);
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const toggleCat = (key) =>
    setCats((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.warn("Tu dispositivo no permite obtener la ubicación");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(p);
        setPoint(p);
      },
      () => toast.warn("No se pudo obtener tu ubicación. Toca el mapa para elegir el punto."),
    );
  };

  const search = async () => {
    if (!point) return toast.warn("Toca el mapa para elegir el punto de búsqueda");
    if (!cats.length) return toast.warn("Selecciona al menos una categoría");
    setSearching(true);
    setPlaces(null);
    try {
      const res = await API.post("/prospects/search", { lat: point.lat, lng: point.lng, radiusKm, categories: cats, country });
      const list = res.data.data.places ?? [];
      setPlaces(list);
      setTruncated(!!res.data.data.truncated);
      setPicked(Object.fromEntries(list.filter((p) => !p.alreadySaved).map((p) => [p.placeId, true])));
      toast.success(list.length ? `${list.length} comercios encontrados` : "No se encontraron comercios con nombre en esa zona");
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al buscar comercios");
    } finally {
      setSearching(false);
    }
  };

  const toggleOne = (id) => setPicked((prev) => ({ ...prev, [id]: !prev[id] }));
  const selectedPlaces = (places ?? []).filter((p) => picked[p.placeId]);

  const saveSelected = async () => {
    if (!selectedPlaces.length) return toast.warn("Selecciona al menos un comercio");
    setSaving(true);
    try {
      const res = await API.post("/prospects/import", { places: selectedPlaces });
      const { created = 0, updated = 0, enriched = 0 } = res.data.data ?? {};
      toast.success(`${created} nuevos guardados${updated ? ` · ${updated} actualizados` : ""}${enriched ? ` · ${enriched} con teléfono/web de Google` : ""}`);
      setPlaces((prev) => prev.map((p) => (picked[p.placeId] ? { ...p, alreadySaved: true } : p)));
      setPicked({});
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al guardar los comercios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-bold text-gray-800 text-lg">Buscar comercios por zona</h3>
            <p className="text-sm text-gray-500">Toca el mapa para elegir el punto y busca alrededor, en cualquier ciudad.</p>
          </div>
          <button onClick={useMyLocation} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
            <MapPin size={15} /> Mi ubicación
          </button>
        </div>

        <div className="h-[300px] sm:h-[420px] w-full rounded-xl overflow-hidden border border-gray-200">
          <MapContainer center={[center.lat, center.lng]} zoom={15} scrollWheelZoom className="h-full w-full">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapRecenter center={center} />
            <MapClickPicker onPick={setPoint} />
            {point && (
              <>
                <Circle center={[point.lat, point.lng]} radius={radiusKm * 1000} pathOptions={{ color: "#fec400", fillOpacity: 0.08 }} />
                <CircleMarker center={[point.lat, point.lng]} radius={7} pathOptions={{ color: "#1f2937", fillColor: "#fec400", fillOpacity: 1 }} />
              </>
            )}
            {(places ?? []).map((p) => (
              <CircleMarker
                key={p.placeId}
                center={[p.lat, p.lng]}
                radius={5}
                pathOptions={{ color: p.alreadySaved ? "#9ca3af" : picked[p.placeId] ? "#16a34a" : "#dc2626", fillOpacity: 0.9 }}
                eventHandlers={{ click: () => toggleOne(p.placeId) }}
              >
                <Tooltip>{p.name}</Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        {point && (
          <p className="text-xs text-gray-500 mt-2">
            Punto: {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Radio de búsqueda: {radiusKm} km</label>
            <input type="range" min={0.2} max={5} step={0.2} value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))} className="w-full accent-supernova-500" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">País de los comercios</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white">
              <option>Colombia</option>
              <option>Venezuela</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-xs font-bold text-gray-500 mb-2">Categorías</p>
          <div className="flex flex-wrap gap-2">
            {PROSPECT_CATEGORY_OPTIONS.map((c) => {
              const active = cats.includes(c.key);
              return (
                <button
                  key={c.key}
                  onClick={() => toggleCat(c.key)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                    active ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={search}
          disabled={searching || !point || !cats.length}
          className="mt-5 w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-supernova-500 hover:bg-supernova-600 text-black font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {searching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          {searching ? "Buscando..." : "Buscar comercios"}
        </button>
      </div>

      {places && (
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h4 className="font-bold text-gray-700">
              {places.length} comercio{places.length !== 1 ? "s" : ""}
              {truncated && <span className="text-xs font-normal text-amber-600 ml-2">(se muestran los primeros 300; reduce el radio)</span>}
            </h4>
            <div className="flex gap-2">
              <button
                onClick={() => setPicked(Object.fromEntries(places.filter((p) => !p.alreadySaved).map((p) => [p.placeId, true])))}
                className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Seleccionar nuevos
              </button>
              <button
                onClick={saveSelected}
                disabled={saving || !selectedPlaces.length}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-black text-xs font-bold disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Guardar {selectedPlaces.length} seleccionado{selectedPlaces.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>

          {places.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No se encontraron comercios con nombre en esa zona. Prueba con más radio o más categorías.</p>
          ) : (
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {places.map((p) => (
                <label key={p.placeId} className={`flex gap-3 p-3 rounded-xl border cursor-pointer transition ${p.alreadySaved ? "border-gray-100 bg-gray-50 opacity-70" : picked[p.placeId] ? "border-supernova-300 bg-supernova-50/40" : "border-gray-200 hover:bg-gray-50"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 accent-supernova-500 w-4 h-4 shrink-0"
                    checked={!!picked[p.placeId]}
                    onChange={() => toggleOne(p.placeId)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-gray-800 text-sm truncate">{p.name}</p>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.categoryLabel}</span>
                      {p.alreadySaved && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Ya guardado</span>}
                    </div>
                    <div className="mt-1 grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-500">
                      {p.address && <span className="flex items-center gap-1 min-w-0"><MapPin size={11} className="shrink-0" /><span className="truncate">{p.address}</span></span>}
                      {p.phone && <span className="flex items-center gap-1"><Phone size={11} />{p.phone}</span>}
                      {p.email && <span className="flex items-center gap-1 min-w-0"><Mail size={11} className="shrink-0" /><span className="truncate">{p.email}</span></span>}
                      {p.website && <span className="flex items-center gap-1 min-w-0"><Globe size={11} className="shrink-0" /><span className="truncate">{p.website}</span></span>}
                      {p.openingHours && <span className="flex items-center gap-1 min-w-0"><Clock size={11} className="shrink-0" /><span className="truncate">{p.openingHours}</span></span>}
                      {!p.phone && !p.email && !p.website && <span className="text-gray-400 italic">Sin teléfono ni correo registrados en el mapa</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: Prospectos ────────────────────────────────────────────────────────────
function ProspectsTab() {
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCity)   params.city   = filterCity;
      const res = await API.get("/prospects", { params });
      setProspects(res.data.data.prospects);
      setTotal(res.data.data.total);
    } catch { toast.error("Error al cargar prospectos"); }
    finally { setLoading(false); }
  }, [filterStatus, filterCity]);

  useEffect(() => { load(); }, [load]);

  const filtered = prospects.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.city.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map((p) => p.id));

  const handleGenerateBulk = async () => {
    const ids = selected.length > 0 ? selected : filtered.map((p) => p.id);
    if (!ids.length) return toast.warn("No hay prospectos seleccionados");
    setGenerating(true);
    try {
      const res = await API.post("/prospects/generate-bulk", { prospectIds: ids });
      const ok = res.data.data.results.filter((r) => r.success).length;
      toast.success(`Mensajes generados: ${ok}/${ids.length}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al generar mensajes");
    } finally { setGenerating(false); }
  };

  const handleSendBulk = async (channels) => {
    const ids = selected.length > 0 ? selected : filtered.filter((p) => p.messageText).map((p) => p.id);
    if (!ids.length) return toast.warn("No hay prospectos con mensajes generados");
    setSending(true);
    try {
      const res = await API.post("/prospects/outreach-bulk", { prospectIds: ids, channels });
      const ok = res.data.data.results.filter((r) => r.whatsapp || r.email).length;
      toast.success(`Enviados: ${ok}/${ids.length}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al enviar mensajes");
    } finally { setSending(false); }
  };

  const saveEdit = async (id) => {
    try {
      await API.put(`/prospects/${id}`, { notes: editNotes, status: editStatus });
      toast.success("Guardado");
      setEditingId(null);
      load();
    } catch { toast.error("Error al guardar"); }
  };

  return (
    <div className="space-y-4">
      {/* Filters + Actions */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-40">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o ciudad..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-supernova-300"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-supernova-300"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>

          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-supernova-300"
          >
            <option value="">Todas las ciudades</option>
            {[...new Set(prospects.map((p) => p.city).filter(Boolean))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <button onClick={load} className="p-2 text-gray-500 hover:text-gray-700 transition">
            <RefreshCw size={17} />
          </button>
        </div>

        {/* Bulk actions */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">{selected.length > 0 ? `${selected.length} seleccionados` : `${filtered.length} prospectos`}</span>
          <button
            onClick={handleGenerateBulk}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 text-violet-700 text-xs font-semibold rounded-lg hover:bg-violet-200 transition disabled:opacity-50"
          >
            {generating ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
            Generar mensajes (IA)
          </button>
          <button
            onClick={() => handleSendBulk(["whatsapp"])}
            disabled={sending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-xs font-semibold rounded-lg hover:bg-green-200 transition disabled:opacity-50"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Enviar WhatsApp
          </button>
          <button
            onClick={() => handleSendBulk(["email"])}
            disabled={sending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg hover:bg-blue-200 transition disabled:opacity-50"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
            Enviar Email
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-gray-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Users size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">Sin prospectos</p>
            <p className="text-xs mt-1">Ve a "Descubrir" para buscar restaurantes</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left">
                    <input type="checkbox" checked={selected.length === filtered.length} onChange={toggleAll} className="rounded" />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Restaurante</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Ciudad</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Contacto</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Mensaje</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="rounded" />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800 truncate max-w-[200px]">{p.name}</p>
                        {p.rating && (
                          <span className="flex items-center gap-1 text-xs text-amber-600 mt-0.5">
                            <Star size={11} fill="currentColor" /> {p.rating}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-gray-600 text-xs">
                          <MapPin size={12} /> {p.city}
                        </span>
                      </td>
                      <td className="px-4 py-3 space-y-0.5">
                        {p.phone && (
                          <span className="flex items-center gap-1 text-xs text-gray-600">
                            <Phone size={11} /> {p.phone}
                          </span>
                        )}
                        {p.email && (
                          <span className="flex items-center gap-1 text-xs text-gray-600 truncate max-w-[150px]">
                            <Mail size={11} /> {p.email}
                          </span>
                        )}
                        {p.website && (
                          <a href={p.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-500 hover:underline truncate max-w-[150px]">
                            <Globe size={11} /> Web
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[p.status]}`}>
                          {STATUS_LABELS[p.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {p.messageText ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <CheckCircle2 size={13} /> Listo
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Sin mensaje</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => { setEditingId(editingId === p.id ? null : p.id); setEditNotes(p.notes ?? ""); setEditStatus(p.status); }}
                          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>

                    {/* Inline edit row */}
                    {editingId === p.id && (
                      <tr className="bg-violet-50">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="flex flex-wrap gap-3 items-end">
                            <div>
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">Estado</label>
                              <select
                                value={editStatus}
                                onChange={(e) => setEditStatus(e.target.value)}
                                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none"
                              >
                                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                              </select>
                            </div>
                            <div className="flex-1 min-w-48">
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">Notas</label>
                              <input
                                type="text"
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                placeholder="Agrega una nota..."
                                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none"
                              />
                            </div>
                            {p.messageText && (
                              <div className="flex-1 min-w-64">
                                <label className="text-xs font-semibold text-gray-600 mb-1 block">Mensaje generado</label>
                                <p className="text-xs text-gray-600 bg-white rounded-lg p-2 border border-gray-200 max-h-20 overflow-y-auto whitespace-pre-line">{p.messageText}</p>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <button onClick={() => saveEdit(p.id)} className="px-3 py-1.5 bg-supernova-500 text-black text-xs font-bold rounded-lg hover:bg-supernova-600 transition">
                                Guardar
                              </button>
                              <button onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-300 transition">
                                Cancelar
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400 text-right">
            {total} prospectos en total
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Estadísticas ──────────────────────────────────────────────────────────
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/prospects/stats")
      .then((r) => setStats(r.data.data))
      .catch(() => toast.error("Error al cargar estadísticas"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-gray-300" /></div>;
  if (!stats) return null;

  const funnel = [
    { key: "nuevo",      label: "Descubiertos" },
    { key: "contactado", label: "Contactados" },
    { key: "interesado", label: "Interesados" },
    { key: "registrado", label: "Registrados" },
  ];

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {funnel.map(({ key, label }) => {
          const count = stats.byStatus[key] ?? 0;
          const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
          return (
            <div key={key} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <p className="text-3xl font-black text-gray-800">{count.toLocaleString()}</p>
              <p className="text-sm font-medium text-gray-500 mt-0.5">{label}</p>
              <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-supernova-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{pct}% del total</p>
            </div>
          );
        })}
      </div>

      {/* By city */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h4 className="font-bold text-gray-700 mb-4">Prospectos por ciudad</h4>
        <div className="space-y-3">
          {stats.byCity.map(({ city, count }) => {
            const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
            return (
              <div key={city}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-600">{city}</span>
                  <span className="font-bold text-gray-800">{count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-supernova-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversion rate */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h4 className="font-bold text-gray-700 mb-4">Conversión del embudo</h4>
        <div className="flex items-center gap-4 text-sm">
          {funnel.map(({ key, label }, i) => {
            const count = stats.byStatus[key] ?? 0;
            return (
              <React.Fragment key={key}>
                <div className="text-center">
                  <p className="text-2xl font-black text-gray-800">{count}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                </div>
                {i < funnel.length - 1 && <span className="text-gray-300 text-lg">→</span>}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const TABS = [
  { id: "discover",   label: "Descubrir",     icon: Search },
  { id: "prospects",  label: "Prospectos",    icon: Users },
  { id: "stats",      label: "Estadísticas",  icon: TrendingUp },
];

export default function ProspectManager() {
  const [tab, setTab] = useState("discover");

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-black text-gray-800">Captación de Clientes</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Agentes de IA para descubrir, contactar y convertir restaurantes en qkiere.com
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === id ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "discover"  && <DiscoverTab />}
      {tab === "prospects" && <ProspectsTab />}
      {tab === "stats"     && <StatsTab />}
    </div>
  );
}
